import { API_BASE } from './api-base';

/**
 * The `/me/<handle>[/<context>]` page's view beacon — the client half of counting a profile-link view
 * (`pages/api/profile-link-view.ts` is the server half). No React in it, so every rule is unit-tested
 * (test/profile-link-view-beacon.test.ts).
 *
 * Why a beacon and not the server render: the page's card read is cached for 60s per pod, so it cannot count,
 * and a render is also what an unfurler, a mail scanner and a prefetch produce. A beacon fires only from a
 * browser that hydrated the page — the same reasoning as the share card's open beacon (`lib/share-card-view.ts`),
 * whose bot user-agent rule the page and the route both apply too.
 *
 * Once per browser SESSION per link: a reload, or a back-and-forth through "Continue on the web", is the same
 * visit. `sessionStorage` can be missing or throw (a private window, blocked site data, some in-app webviews);
 * a visitor in one of those is counted once per page load, which is the right way for this to degrade.
 */
export const PROFILE_VIEW_STORAGE_PREFIX = 'symphony-profile-view:';

export function profileViewStorageKey(handle: string, context: string | undefined): string {
  return `${PROFILE_VIEW_STORAGE_PREFIX}${handle.toLowerCase()}/${(context ?? '').toLowerCase()}`;
}

function sessionStore(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.sessionStorage;
  } catch {
    return null;
  }
}

/**
 * True when this browser session has not counted this link yet — and marks it counted. `storage` is a test
 * seam; omitted, it is `window.sessionStorage` (or nothing, which counts).
 */
export function claimProfileView(handle: string, context: string | undefined, storage?: Storage | null): boolean {
  const store = storage === undefined ? sessionStore() : storage;
  if (!store) return true;
  const key = profileViewStorageKey(handle, context);
  try {
    if (store.getItem(key)) return false;
  } catch {
    return true;
  }
  try {
    store.setItem(key, '1');
  } catch {
    /* cannot remember: counted once per page load instead */
  }
  return true;
}

type Fetcher = (input: string, init: RequestInit) => Promise<unknown>;

/**
 * Post the view. Fire-and-forget: `keepalive` lets it finish when the visitor taps straight through to WhatsApp
 * or the app, and a failure is swallowed — a statistic never breaks the page. The body is the link and nothing
 * else; who is viewing is the server's to read off the caller's own session.
 */
export function sendProfileView(handle: string, context: string | undefined, fetcher: Fetcher = (input, init) => fetch(input, init)): void {
  try {
    void Promise.resolve(
      fetcher(`${API_BASE}/api/profile-link-view`, {
        method: 'POST',
        credentials: 'include',
        keepalive: true,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(context ? { handle, context } : { handle }),
      }),
    ).catch(() => undefined);
  } catch {
    /* no fetch, nothing counted */
  }
}

/** What the `/me/` page counts: a view, or a tap on one of its three buttons. */
export type ProfileLinkRecord = 'view' | 'app_tap' | 'whatsapp_tap' | 'message_tap';
export type ProfileTapKind = Exclude<ProfileLinkRecord, 'view'>;

/** The route's `kind` → what is counted. Absent or unknown = a view, so the view beacon's body is unchanged. */
export function parseProfileRecordKind(value: unknown): ProfileLinkRecord {
  return value === 'app_tap' || value === 'whatsapp_tap' || value === 'message_tap' ? value : 'view';
}

/** The page's button → the tap it counts. "Continue on the web" opens the profile thread: the MESSAGE button. */
export const PROFILE_CTA_TAP: Record<'app' | 'whatsapp' | 'web', ProfileTapKind> = {
  app: 'app_tap',
  whatsapp: 'whatsapp_tap',
  web: 'message_tap',
};

/**
 * Count ONE tap on a CTA, fired from the button's click before the browser navigates away. Every tap counts (no
 * per-session claim — tapping WhatsApp twice is two intents, and the route's per-visitor limit bounds a script).
 * `keepalive` is what lets it outlive the navigation it precedes — the same delivery `navigator.sendBeacon` gives,
 * but with the JSON content type the route's CSRF check requires (a beacon Blob of `application/json` is refused
 * by some browsers). Fire-and-forget; a failure is swallowed.
 */
export function sendProfileTap(
  handle: string,
  context: string | undefined,
  kind: ProfileTapKind,
  fetcher: Fetcher = (input, init) => fetch(input, init),
): void {
  try {
    void Promise.resolve(
      fetcher(`${API_BASE}/api/profile-link-view`, {
        method: 'POST',
        credentials: 'include',
        keepalive: true,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(context ? { handle, context, kind } : { handle, kind }),
      }),
    ).catch(() => undefined);
  } catch {
    /* no fetch, nothing counted */
  }
}
